import { useEffect, useRef, useState } from "react";
import { Camera, ImageIcon, Loader2, Mail, Pencil, Trash2, UserMinus, UserPlus, X } from "lucide-react";
import { AvatarBubble } from "@/components/chatChrome";
import { ChatWallpaperPicker } from "@/components/ChatWallpaperPicker";
import { ChatGuestRows, ChatMeetingSection } from "@/components/ChatMeetingSection";
import { defaultMeetingLocalValue, type ChatMeeting } from "@/lib/meetingsApi";
import { conversationTitle, memberLabel } from "@/lib/chatFormat";
import type { ChatNickMap } from "@/lib/chatNicknames";
import { normalizeChatPresence, type ChatColleague, type ChatConversation, type ChatPresenceStatus } from "@/lib/chatApi";
import type { ChatWallpaperPrefs } from "@/lib/theme/chatWallpapers";
import {
  copyText,
  createChatGuestInvite,
  listChatGuestInvites,
  reinviteChatGuest,
  revokeChatGuestInvite,
  type ChatGuestInvite,
} from "@/lib/guestsApi";

type Props = {
  selected: ChatConversation;
  selfId: number;
  myLabel: string;
  myStatus: ChatPresenceStatus;
  colleagues: ChatColleague[];
  presence: Record<number, string>;
  renameDraft: string;
  onRenameDraftChange: (value: string) => void;
  wallpaperPrefs: ChatWallpaperPrefs;
  onWallpaperChange: (next: ChatWallpaperPrefs) => void;
  onClose: () => void;
  onRename: () => void;
  onGroupAvatar: (file: File) => void;
  onDelete: () => void;
  onRemoveMember?: (userId: number, name: string) => void;
  onAddMember?: (userId: number) => Promise<void>;
  onOpenAvatarPreview: (url: string | null | undefined, alt?: string) => void;
  suiteUserId?: string;
  meetings?: ChatMeeting[];
  onRefreshMeetings?: () => void;
  onJoinMeeting?: () => void;
  open: boolean;
  nicknames?: ChatNickMap;
  officialName?: (userId: number) => string;
  onSaveNickname?: (userId: number, name: string | null) => void;
};

export function ChatInfoPanel({
  selected,
  selfId,
  myLabel,
  myStatus,
  colleagues,
  presence,
  renameDraft,
  onRenameDraftChange,
  wallpaperPrefs,
  onWallpaperChange,
  onClose,
  onRename,
  onGroupAvatar,
  onDelete,
  onRemoveMember,
  onAddMember,
  onOpenAvatarPreview,
  suiteUserId,
  meetings = [],
  onRefreshMeetings,
  onJoinMeeting,
  open,
  nicknames = {},
  officialName,
  onSaveNickname,
}: Props) {
  const groupAvatarCameraRef = useRef<HTMLInputElement>(null);
  const groupAvatarLibraryRef = useRef<HTMLInputElement>(null);
  const [groupAvatarPickOpen, setGroupAvatarPickOpen] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [addBusyId, setAddBusyId] = useState<number | null>(null);
  const [addHint, setAddHint] = useState<string | null>(null);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestBusy, setGuestBusy] = useState(false);
  const [guestHint, setGuestHint] = useState<string | null>(null);
  const [guests, setGuests] = useState<ChatGuestInvite[]>([]);
  const [revokeBusyId, setRevokeBusyId] = useState<number | null>(null);
  const [reinviteBusyId, setReinviteBusyId] = useState<number | null>(null);
  const dmPeerId =
    selected.type === "dm"
      ? selected.member_ids.map(Number).find((id) => id !== selfId) ?? null
      : null;
  const [nickDraft, setNickDraft] = useState("");
  const [nickEditId, setNickEditId] = useState<number | null>(null);
  const [nickEditDraft, setNickEditDraft] = useState("");
  useEffect(() => {
    if (dmPeerId == null) return;
    setNickDraft(nicknames[dmPeerId] ?? "");
  }, [dmPeerId, selected.id]);
  const [meetStartsAt, setMeetStartsAt] = useState(defaultMeetingLocalValue);
  const [meetDuration, setMeetDuration] = useState(60);
  const [meetTitle, setMeetTitle] = useState("");

  const memberSet = new Set(selected.member_ids);
  const soloHost = selected.member_ids.length <= 1;
  const addable = colleagues.filter((c) => {
    if (c.id === selfId || memberSet.has(c.id)) return false;
    if (!soloHost && selected.client_id) {
      const ids = c.client_ids ?? c.clients?.map((cl) => cl.id) ?? [];
      if (ids.length > 0 && !ids.includes(selected.client_id)) return false;
    }
    return true;
  });
  const roomFull = Boolean(selected.is_room && selected.member_ids.length >= 4);
  const refreshGuests = () => {
    if (!suiteUserId || selected.type !== "group") return;
    void listChatGuestInvites(suiteUserId, selected.id).then((r) => {
      if (r.success && r.data) setGuests(r.data);
    });
  };

  useEffect(() => {
    if (!open || selected.type !== "group") {
      setGuests([]);
      setShowAddMember(false);
      setShowAddGuest(false);
      return;
    }
    refreshGuests();
    const id = window.setInterval(() => refreshGuests(), 4000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recharger à l’ouverture / changement de conv
  }, [open, selected.id, selected.type, suiteUserId]);

  return (
    <aside
      className={`chat-panel chat-info-drawer flex flex-col border-l ${open ? "is-open" : ""}`}
      aria-hidden={!open}
      inert={!open ? true : undefined}
    >
      <div className="chat-info-drawer__inner flex min-h-0 flex-1 flex-col">
      <header className="flex items-center justify-between border-b border-[var(--chat-border)] px-3 py-3">
        <h3 className="text-sm font-semibold">
          {selected.type === "group" ? "Infos du groupe" : "Infos"}
        </h3>
        <button type="button" onClick={onClose} aria-label="Fermer">
          <X className="size-4 text-[var(--chat-muted)]" />
        </button>
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
        {selected.type === "group" ? (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              className="relative cursor-pointer"
              onClick={() => {
                const cameraOk =
                  typeof window !== "undefined" &&
                  window.matchMedia("(pointer: coarse), (max-width: 767px)").matches;
                if (cameraOk) {
                  setGroupAvatarPickOpen((v) => !v);
                  return;
                }
                groupAvatarLibraryRef.current?.click();
              }}
              aria-expanded={groupAvatarPickOpen}
              title="Changer la photo du groupe"
            >
              <span className="relative flex size-20 items-center justify-center overflow-hidden rounded-full bg-[var(--chat-accent-soft)] text-2xl font-semibold text-[var(--chat-accent-strong)]">
                {selected.avatar_url ? (
                  <img src={selected.avatar_url} alt="" className="size-full object-cover" />
                ) : (
                  conversationTitle(selected, selfId).slice(0, 1).toUpperCase()
                )}
                <span className="absolute inset-x-0 bottom-0 flex justify-center bg-black/45 py-1">
                  <Camera className="size-3.5 text-white" />
                </span>
              </span>
            </button>
            <input
              ref={groupAvatarCameraRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                setGroupAvatarPickOpen(false);
                if (f) onGroupAvatar(f);
              }}
            />
            <input
              ref={groupAvatarLibraryRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                setGroupAvatarPickOpen(false);
                if (f) onGroupAvatar(f);
              }}
            />
            {groupAvatarPickOpen ? (
              <div className="flex w-full max-w-xs flex-col gap-1 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] p-1.5 md:hidden">
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium hover:bg-[var(--chat-surface-elevated)]"
                  onClick={() => groupAvatarCameraRef.current?.click()}
                >
                  <Camera className="size-4 text-[var(--chat-accent-strong)]" />
                  Prendre une photo
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium hover:bg-[var(--chat-surface-elevated)]"
                  onClick={() => groupAvatarLibraryRef.current?.click()}
                >
                  <ImageIcon className="size-4 text-[var(--chat-accent-strong)]" />
                  Choisir dans la galerie
                </button>
              </div>
            ) : (
              <p className="text-xs text-[var(--chat-muted)]">Photo du groupe</p>
            )}
          </div>
        ) : null}

        {selected.type === "group" ? (
          <div className="space-y-2">
            <label className="block space-y-1">
              <span className="text-xs text-[var(--chat-muted)]">Nom du groupe</span>
              <input
                value={renameDraft}
                onChange={(e) => onRenameDraftChange(e.target.value)}
                className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)]"
              />
            </label>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--chat-accent-soft)] px-3 py-1.5 text-xs font-medium text-[var(--chat-accent-strong)]"
              onClick={onRename}
            >
              <Pencil className="size-3.5" />
              Renommer
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-center text-sm font-medium">{conversationTitle(selected, selfId)}</p>
            {dmPeerId != null && onSaveNickname ? (
              <label className="block space-y-1">
                <span className="text-xs text-[var(--chat-muted)]">Surnom (visible seulement par vous)</span>
                <input
                  value={nickDraft}
                  onChange={(e) => setNickDraft(e.target.value)}
                  placeholder={officialName?.(dmPeerId) || "Surnom"}
                  maxLength={40}
                  className="w-full rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--chat-accent)]"
                />
                <span className="block text-[10px] leading-snug text-[var(--chat-muted)]">
                  Le nom Suite ne change pas. Lui et les autres apps ne voient rien.
                </span>
                <span className="flex flex-wrap gap-2 pt-0.5">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--chat-accent-soft)] px-3 py-1.5 text-xs font-medium text-[var(--chat-accent-strong)]"
                    onClick={() => onSaveNickname(dmPeerId, nickDraft)}
                  >
                    <Pencil className="size-3.5" />
                    Enregistrer
                  </button>
                  {nicknames[dmPeerId] ? (
                    <button
                      type="button"
                      className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
                      onClick={() => {
                        setNickDraft("");
                        onSaveNickname(dmPeerId, null);
                      }}
                    >
                      Retirer
                    </button>
                  ) : null}
                </span>
              </label>
            ) : null}
          </div>
        )}

        <ChatWallpaperPicker prefs={wallpaperPrefs} onChange={onWallpaperChange} />

        {selected.type === "group" && suiteUserId && onRefreshMeetings && onJoinMeeting ? (
          <ChatMeetingSection
            suiteUserId={suiteUserId}
            conversationId={selected.id}
            meetings={meetings}
            selfId={selfId}
            members={selected.member_ids.map((id) => {
              const member = selected.members?.find((m) => m.id === id);
              const name =
                memberLabel(member, "") ||
                colleagues.find((c) => c.id === id)?.username ||
                (id === selfId ? myLabel : `#${id}`);
              return { id, name };
            })}
            onRefresh={onRefreshMeetings}
            onJoin={onJoinMeeting}
            draftStartsAt={meetStartsAt}
            onDraftStartsAt={setMeetStartsAt}
            draftDuration={meetDuration}
            onDraftDuration={setMeetDuration}
            draftTitle={meetTitle}
            onDraftTitle={setMeetTitle}
            guests={guests}
            onRefreshGuests={refreshGuests}
          />
        ) : null}

        {selected.type === "group" ? (
          <>
            <p className="text-xs text-[var(--chat-muted)]">{selected.member_ids.length} participants</p>
            <ul className="space-y-2">
              {selected.member_ids.map((id) => {
                const member = selected.members?.find((m) => m.id === id);
                const name =
                  memberLabel(member, "") ||
                  colleagues.find((c) => c.id === id)?.username ||
                  (id === selfId ? myLabel : `#${id}`);
                return (
                  <li key={id} className="flex items-center gap-2 text-sm">
                    <AvatarBubble
                      label={name}
                      url={member?.avatar_url}
                      presence={id === selfId ? myStatus : normalizeChatPresence(presence[id])}
                      size="sm"
                      onClick={
                        member?.avatar_url
                          ? () => onOpenAvatarPreview(member.avatar_url, name)
                          : undefined
                      }
                    />
                    <span className="min-w-0 flex-1">
                      {nickEditId === id && onSaveNickname ? (
                        <span className="flex flex-col gap-1">
                          <input
                            value={nickEditDraft}
                            onChange={(e) => setNickEditDraft(e.target.value)}
                            placeholder={officialName?.(id) || name}
                            maxLength={40}
                            className="w-full rounded-lg border border-[var(--chat-border)] bg-[var(--chat-bg)] px-2 py-1 text-sm outline-none focus:border-[var(--chat-accent)]"
                          />
                          <span className="flex gap-2">
                            <button
                              type="button"
                              className="text-[11px] font-medium text-[var(--chat-accent-strong)]"
                              onClick={() => {
                                onSaveNickname(id, nickEditDraft);
                                setNickEditId(null);
                              }}
                            >
                              OK
                            </button>
                            <button
                              type="button"
                              className="text-[11px] text-[var(--chat-muted)]"
                              onClick={() => setNickEditId(null)}
                            >
                              Annuler
                            </button>
                          </span>
                        </span>
                      ) : (
                        <span className="truncate">
                          {name}
                          {id === selfId ? (
                            <span className="text-xs text-[var(--chat-muted)]"> (vous)</span>
                          ) : nicknames[id] ? (
                            <span className="text-[10px] text-[var(--chat-muted)]"> · surnom</span>
                          ) : null}
                        </span>
                      )}
                    </span>
                    {id !== selfId && onSaveNickname && nickEditId !== id ? (
                      <button
                        type="button"
                        className="shrink-0 rounded-lg p-1.5 text-[var(--chat-muted)] hover:bg-[var(--chat-accent-soft)] hover:text-[var(--chat-accent-strong)]"
                        title="Surnom (seulement pour vous)"
                        aria-label={`Surnom de ${name}`}
                        onClick={() => {
                          setNickEditId(id);
                          setNickEditDraft(nicknames[id] ?? "");
                        }}
                      >
                        <Pencil className="size-3.5" />
                      </button>
                    ) : null}
                    {id !== selfId && selected.created_by === selfId && onRemoveMember ? (
                      <button
                        type="button"
                        className="shrink-0 rounded-lg p-1.5 text-[var(--chat-muted)] hover:bg-[var(--chat-danger)]/10 hover:text-[var(--chat-danger)]"
                        title={`Retirer ${name}`}
                        aria-label={`Retirer ${name}`}
                        onClick={() => onRemoveMember(id, name)}
                      >
                        <UserMinus className="size-4" />
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {onAddMember ? (
              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[var(--chat-border)] px-3 py-2 text-xs font-medium text-[var(--chat-accent-strong)] hover:bg-[var(--chat-accent-soft)] disabled:opacity-50"
                  disabled={roomFull}
                  onClick={() => {
                    setAddHint(null);
                    setShowAddMember((v) => !v);
                  }}
                >
                  <UserPlus className="size-3.5" />
                  {roomFull
                    ? "Salle pleine (max 4)"
                    : showAddMember
                      ? "Fermer"
                      : "Ajouter un membre"}
                </button>
                {showAddMember && !roomFull ? (
                  <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-[var(--chat-border)] p-1">
                    {addable.length === 0 ? (
                      <p className="px-2 py-2 text-xs text-[var(--chat-muted)]">
                        {soloHost
                          ? "Aucun collègue Chat à ajouter."
                          : "Aucun contact de ce client Chat (Pilotage) à ajouter."}
                      </p>
                    ) : (
                      addable.map((c) => {
                        const name = c.display_name?.trim() || c.username || `#${c.id}`;
                        const busy = addBusyId === c.id;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-[var(--chat-surface-elevated)] disabled:opacity-50"
                            disabled={addBusyId != null}
                            onClick={() => {
                              void (async () => {
                                setAddBusyId(c.id);
                                setAddHint(null);
                                try {
                                  await onAddMember(c.id);
                                  setAddHint(
                                    meetings.length > 0
                                      ? `${name} a rejoint le groupe — pas ajouté à la visio. Cochez-le dans Visio planifiée si besoin.`
                                      : `${name} a rejoint le groupe.`
                                  );
                                } catch (e) {
                                  setAddHint(
                                    e instanceof Error ? e.message : "Impossible d’ajouter."
                                  );
                                } finally {
                                  setAddBusyId(null);
                                }
                              })();
                            }}
                          >
                            <AvatarBubble
                              label={name}
                              url={c.avatar_url}
                              presence={normalizeChatPresence(presence[c.id] ?? c.presence)}
                              size="sm"
                            />
                            <span className="min-w-0 flex-1 truncate">{name}</span>
                            {busy ? (
                              <Loader2 className="size-3.5 animate-spin text-[var(--chat-muted)]" />
                            ) : (
                              <UserPlus className="size-3.5 shrink-0 text-[var(--chat-muted)]" />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                ) : null}
                {addHint ? <p className="text-xs text-[var(--chat-muted)]">{addHint}</p> : null}
              </div>
            ) : null}

            {suiteUserId ? (
              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[var(--chat-border)] px-3 py-2 text-xs font-medium text-[var(--chat-accent-strong)] hover:bg-[var(--chat-accent-soft)] disabled:opacity-50"
                  disabled={roomFull}
                  onClick={() => {
                    setGuestHint(null);
                    setShowAddGuest((v) => !v);
                  }}
                >
                  <Mail className="size-3.5" />
                  {roomFull
                    ? "Salle pleine (max 4)"
                    : showAddGuest
                      ? "Fermer"
                      : "Ajouter un invité"}
                </button>
                {showAddGuest && !roomFull ? (
                  <div className="space-y-2 rounded-xl border border-[var(--chat-border)] p-2.5">
                    <p className="text-[11px] leading-relaxed text-[var(--chat-muted)]">
                      Invité ponctuel — sans horaire, le lien s’active dès que vous lancez la visio.
                    </p>
                    <div className="flex gap-1.5">
                      <input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="e-mail de l’invité"
                        className="min-w-0 flex-1 rounded-lg border border-[var(--chat-border)] bg-[var(--chat-bg)] px-2.5 py-1.5 text-sm outline-none focus:border-[var(--chat-accent)]"
                      />
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg bg-[var(--chat-accent-soft)] px-2.5 py-1.5 text-xs font-medium text-[var(--chat-accent-strong)] disabled:opacity-50"
                        disabled={guestBusy}
                        onClick={() => {
                          void (async () => {
                            const email = guestEmail.trim();
                            if (!email.includes("@")) {
                              setGuestHint("Indiquez un e-mail.");
                              return;
                            }
                            setGuestBusy(true);
                            setGuestHint(null);
                            const g = await createChatGuestInvite(suiteUserId, {
                              conversationId: selected.id,
                              email,
                            });
                            setGuestBusy(false);
                            if (!g.success || !g.data) {
                              setGuestHint(g.message || "Impossible d’inviter.");
                              return;
                            }
                            setGuestEmail("");
                            await copyText(g.data.join_url);
                            setGuestHint(
                              g.data.mailed ? "Invitation envoyée · lien copié" : "Lien copié"
                            );
                            refreshGuests();
                          })();
                        }}
                      >
                        {guestBusy ? <Loader2 className="size-3 animate-spin" /> : <UserPlus className="size-3" />}
                        Inviter
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center rounded-lg border border-[var(--chat-border)] px-2.5 py-1.5 text-xs font-medium disabled:opacity-50"
                        disabled={guestBusy}
                        onClick={() => {
                          void (async () => {
                            setGuestBusy(true);
                            setGuestHint(null);
                            const g = await createChatGuestInvite(suiteUserId, {
                              conversationId: selected.id,
                            });
                            setGuestBusy(false);
                            if (!g.success || !g.data) {
                              setGuestHint(g.message || "Impossible de créer le lien.");
                              return;
                            }
                            await copyText(g.data.join_url);
                            setGuestHint("Lien copié — dès que vous lancez la visio");
                            refreshGuests();
                          })();
                        }}
                      >
                        Lien
                      </button>
                    </div>
                    {guestHint ? <p className="text-xs text-[var(--chat-muted)]">{guestHint}</p> : null}
                    <ChatGuestRows
                      guests={guests.filter((g) => !g.meeting_id)}
                      locked={guestBusy || revokeBusyId != null || reinviteBusyId != null}
                      revokeBusyId={revokeBusyId}
                      reinviteBusyId={reinviteBusyId}
                      onRevoke={(id) => {
                        void (async () => {
                          setRevokeBusyId(id);
                          const r = await revokeChatGuestInvite(suiteUserId, selected.id, id);
                          setRevokeBusyId(null);
                          if (!r.success) {
                            setGuestHint(r.message || "Impossible de retirer.");
                            return;
                          }
                          refreshGuests();
                        })();
                      }}
                      onReinvite={(g) => {
                        void (async () => {
                          setReinviteBusyId(g.id);
                          setGuestHint(null);
                          const r = await reinviteChatGuest(suiteUserId, selected.id, g.id);
                          setReinviteBusyId(null);
                          if (!r.success || !r.data) {
                            setGuestHint(r.message || "Impossible de relancer.");
                            return;
                          }
                          if (r.data.join_url) await copyText(r.data.join_url);
                          setGuestHint(
                            r.data.mailed
                              ? "Invitation relancée — e-mail envoyé, lien copié."
                              : "Nouveau lien copié."
                          );
                          refreshGuests();
                        })();
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}

        <button
          type="button"
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--chat-danger)]/40 px-3 py-2.5 text-sm text-[var(--chat-danger)] hover:bg-[var(--chat-danger)]/10"
          onClick={onDelete}
        >
          <Trash2 className="size-4" />
          {selected.type === "group"
            ? selected.created_by === selfId
              ? "Supprimer le groupe"
              : "Quitter le groupe"
            : "Supprimer la conversation"}
        </button>
      </div>
      </div>
    </aside>
  );
}
